"use client";

import { createContext, useContext } from "react";
import { useSession } from "./use-session";

/**
 * Whether the browser sent a session cookie, known on the server. It lets the first paint of the public pages
 * (pricing, terms, privacy, refund) already be the right one for a signed-in person, instead of showing the
 * signed-out header and sidebar-less page and swapping once the session request returns.
 * It is only a hint: the real session still decides once it loads.
 */
const AuthHintContext = createContext(false);

export function AuthHintProvider({ value, children }: { value: boolean; children: React.ReactNode }) {
	return <AuthHintContext.Provider value={value}>{children}</AuthHintContext.Provider>;
}

export function useAuthHint(): boolean {
	return useContext(AuthHintContext);
}

/** Signed in or not, for wording on public pages: the server's cookie hint until the session answers, then the session itself. */
export function useSignedIn(): boolean {
	const { user, status } = useSession();
	const hint = useAuthHint();
	return status === "loading" ? hint : Boolean(user);
}
